import { useState } from "react";
import {
  Box,
  Popover,
  Stack,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import FormatColorText from "@mui/icons-material/FormatColorText";
import FormatColorResetOutlined from "@mui/icons-material/FormatColorResetOutlined";

const PRESET_COLORS = [
  "#000000",
  "#434343",
  "#666666",
  "#999999",
  "#FF0000",
  "#E65100",
  "#F9A825",
  "#2E7D32",
  "#1565C0",
  "#6A1B9A",
  "#00838F",
  "#AD1457",
];

const ColorPickerButton = ({ editor, theme }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const currentColor =
    editor?.getAttributes("textStyle")?.color || "#000000";

  const applyColor = (color) => {
    if (!editor || !color) return;
    editor.chain().focus().setColor(color).run();
  };

  const clearColor = () => {
    if (!editor) return;
    editor.chain().focus().unsetColor().run();
    setAnchorEl(null);
  };

  return (
    <>
      <Tooltip title="Text colour">
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{
            borderRadius: "8px",
            color: "text.secondary",
            "&:hover": { bgcolor: "action.hover" },
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <FormatColorText fontSize="small" />
            <Box
              sx={{
                width: 14,
                height: 3,
                borderRadius: 1,
                bgcolor: currentColor,
                mt: "-2px",
              }}
            />
          </Box>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              p: 1.5,
              borderRadius: "12px",
              boxShadow: "1px 5px 5px 1px rgba(0,0,0,0.12)",
              width: 240,
            },
          },
        }}
      >
        <Stack spacing={1.25}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Text colour
          </Typography>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              component="input"
              type="color"
              value={/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(currentColor) ? currentColor : "#000000"}
              onChange={(e) => applyColor(e.target.value)}
              sx={{
                width: 44,
                height: 44,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "10px",
                p: 0.25,
                bgcolor: "#fff",
                cursor: "pointer",
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Colour wheel
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pick any colour for selected text.
              </Typography>
            </Box>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Presets
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(6, 1fr)",
              gap: 0.75,
            }}
          >
            {PRESET_COLORS.map((color) => (
              <Box
                key={color}
                component="button"
                type="button"
                onClick={() => {
                  applyColor(color);
                  setAnchorEl(null);
                }}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "8px",
                  border:
                    currentColor?.toLowerCase() === color.toLowerCase()
                      ? `2px solid ${theme?.palette?.primary?.main || "#FF0000"}`
                      : "1px solid #ddd",
                  bgcolor: color,
                  cursor: "pointer",
                  p: 0,
                }}
                aria-label={color}
              />
            ))}
          </Box>

          <Button
            size="small"
            startIcon={<FormatColorResetOutlined />}
            onClick={clearColor}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "10px",
              alignSelf: "flex-start",
            }}
          >
            Remove colour
          </Button>
        </Stack>
      </Popover>
    </>
  );
};

export default ColorPickerButton;
